import React, { useState, useEffect } from 'react';
import { popiaAPI } from '../utils/api';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { format } from 'date-fns';
import {
  Shield,
  FileText,
  Download,
  CheckCircle2,
  XCircle,
  Clock,
  Send,
  Eye,
  AlertTriangle,
} from 'lucide-react';

const POPIACompliance = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('privacy');
  const [loading, setLoading] = useState(false);
  const [privacyPolicy, setPrivacyPolicy] = useState(null);
  const [consents, setConsents] = useState([]);
  const [dataRequests, setDataRequests] = useState([]);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [requestForm, setRequestForm] = useState({ type: 'access', description: '' });

  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [policyRes, consentsRes, requestsRes] = await Promise.all([
        popiaAPI.getPrivacyPolicy(),
        popiaAPI.getMyConsents(),
        popiaAPI.getDataRequests({}),
      ]);
      setPrivacyPolicy(policyRes.data);
      setConsents(consentsRes.data);
      setDataRequests(requestsRes.data);
    } catch (error) {
      console.error('Failed to load POPIA data');
    }
  };

  const handleConsent = async (consentType, consented) => {
    try {
      await popiaAPI.recordConsent(consentType, consented);
      toast.success(`Consent ${consented ? 'given' : 'withdrawn'}`);
      loadData();
    } catch (error) {
      toast.error('Failed to update consent');
    }
  };

  const handleSubmitRequest = async () => {
    try {
      setLoading(true);
      await popiaAPI.createDataRequest(requestForm.type, requestForm.description);
      toast.success('Request submitted successfully');
      setShowRequestModal(false);
      setRequestForm({ type: 'access', description: '' });
      loadData();
    } catch (error) {
      toast.error('Failed to submit request');
    } finally {
      setLoading(false);
    }
  };

  const handleExportData = async () => {
    try {
      setLoading(true);
      const res = await popiaAPI.exportMyData();
      // Download as JSON file
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `my-data-export-${format(new Date(), 'yyyy-MM-dd')}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Data exported successfully');
    } catch (error) {
      toast.error('Failed to export data');
    } finally {
      setLoading(false);
    }
  };

  const handleProcessRequest = async (requestId, status) => {
    const notes = prompt('Enter response notes:');
    if (notes === null) return;
    try {
      await popiaAPI.processDataRequest(requestId, status, notes);
      toast.success('Request processed');
      loadData();
    } catch (error) {
      toast.error('Failed to process request');
    }
  };

  const getConsentStatus = (type) => {
    const consent = consents.find(c => c.consent_type === type);
    return consent?.consented || false;
  };

  const requestTypeLabels = {
    access: 'Access My Data',
    rectification: 'Correct My Data',
    erasure: 'Delete My Data',
    portability: 'Export My Data',
  };

  const statusColors = {
    pending: 'bg-amber-100 text-amber-700',
    processing: 'bg-blue-100 text-blue-700',
    completed: 'bg-green-100 text-green-700',
    rejected: 'bg-red-100 text-red-700',
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
          <Shield className="w-6 h-6 text-blue-600" />
          POPIA Compliance
        </h1>
        <p className="text-slate-600">Manage your data privacy and rights under POPIA</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-200 mb-6">
        <nav className="flex gap-4 overflow-x-auto">
          <button
            onClick={() => setActiveTab('privacy')}
            className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors whitespace-nowrap ${
              activeTab === 'privacy' ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-600'
            }`}
          >
            <FileText className="w-4 h-4" />
            Privacy Policy
          </button>
          <button
            onClick={() => setActiveTab('consent')}
            className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors whitespace-nowrap ${
              activeTab === 'consent' ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-600'
            }`}
          >
            <CheckCircle2 className="w-4 h-4" />
            My Consents
          </button>
          <button
            onClick={() => setActiveTab('rights')}
            className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors whitespace-nowrap ${
              activeTab === 'rights' ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-600'
            }`}
          >
            <Shield className="w-4 h-4" />
            My Rights
          </button>
          {isAdmin && (
            <button
              onClick={() => setActiveTab('requests')}
              className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors whitespace-nowrap ${
                activeTab === 'requests' ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-600'
              }`}
            >
              <Clock className="w-4 h-4" />
              Data Requests
            </button>
          )}
        </nav>
      </div>

      {/* Privacy Policy Tab */}
      {activeTab === 'privacy' && privacyPolicy && (
        <div className="bg-white border border-slate-200 rounded-xl">
          <div className="p-4 border-b border-slate-200 flex items-center justify-between">
            <div>
              <h2 className="font-semibold text-slate-900">Privacy Policy</h2>
              <p className="text-sm text-slate-500">
                Version {privacyPolicy.version} • Effective {privacyPolicy.effective_date}
              </p>
            </div>
          </div>
          <div className="p-6 prose prose-slate max-w-none">
            <div className="whitespace-pre-wrap text-slate-700 text-sm leading-relaxed">
              {privacyPolicy.content}
            </div>
          </div>
        </div>
      )}

      {/* Consents Tab */}
      {activeTab === 'consent' && (
        <div className="space-y-4">
          {[
            { type: 'privacy_policy', title: 'Privacy Policy', description: 'I have read and accept the privacy policy' },
            { type: 'data_processing', title: 'Data Processing', description: 'I consent to the processing of my personal data for employment purposes' },
            { type: 'location_tracking', title: 'Location Tracking', description: 'I consent to location tracking for clock in/out verification' },
          ].map((item) => (
            <div key={item.type} className="bg-white border border-slate-200 rounded-xl p-6">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-semibold text-slate-900">{item.title}</h3>
                  <p className="text-sm text-slate-600 mt-1">{item.description}</p>
                </div>
                <button
                  onClick={() => handleConsent(item.type, !getConsentStatus(item.type))}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                    getConsentStatus(item.type)
                      ? 'bg-green-100 text-green-700 hover:bg-green-200'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                >
                  {getConsentStatus(item.type) ? (
                    <>
                      <CheckCircle2 className="w-4 h-4" />
                      Consented
                    </>
                  ) : (
                    <>
                      <XCircle className="w-4 h-4" />
                      Not Consented
                    </>
                  )}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* My Rights Tab */}
      {activeTab === 'rights' && (
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
            <h3 className="font-semibold text-blue-900 mb-2">Your Rights Under POPIA</h3>
            <ul className="text-sm text-blue-800 space-y-2">
              <li>• <strong>Right to Access:</strong> Request a copy of your personal data</li>
              <li>• <strong>Right to Rectification:</strong> Request correction of inaccurate data</li>
              <li>• <strong>Right to Erasure:</strong> Request deletion of your data</li>
              <li>• <strong>Right to Portability:</strong> Export your data in a machine-readable format</li>
            </ul>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <button
              onClick={handleExportData}
              disabled={loading}
              className="flex items-center justify-center gap-2 bg-white border border-slate-200 rounded-xl p-6 hover:bg-slate-50 transition-colors"
            >
              <Download className="w-6 h-6 text-blue-600" />
              <div className="text-left">
                <p className="font-medium text-slate-900">Export My Data</p>
                <p className="text-sm text-slate-500">Download all your personal data</p>
              </div>
            </button>

            <button
              onClick={() => setShowRequestModal(true)}
              className="flex items-center justify-center gap-2 bg-white border border-slate-200 rounded-xl p-6 hover:bg-slate-50 transition-colors"
            >
              <Send className="w-6 h-6 text-blue-600" />
              <div className="text-left">
                <p className="font-medium text-slate-900">Submit Request</p>
                <p className="text-sm text-slate-500">Request data access, correction, or deletion</p>
              </div>
            </button>
          </div>

          {/* My Requests */}
          <div className="bg-white border border-slate-200 rounded-xl">
            <div className="p-4 border-b border-slate-200">
              <h3 className="font-semibold text-slate-900">My Requests</h3>
            </div>
            {dataRequests.filter(r => r.user_id === user?.id).length > 0 ? (
              <div className="divide-y divide-slate-200">
                {dataRequests.filter(r => r.user_id === user?.id).map((req) => (
                  <div key={req.id} className="p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium text-slate-900">{requestTypeLabels[req.request_type]}</p>
                        {req.description && <p className="text-sm text-slate-600 mt-1">{req.description}</p>}
                        <p className="text-xs text-slate-400 mt-2">
                          Submitted {format(new Date(req.requested_at), 'MMM dd, yyyy')}
                        </p>
                      </div>
                      <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColors[req.status]}`}>
                        {req.status}
                      </span>
                    </div>
                    {req.response_notes && (
                      <div className="mt-3 p-3 bg-slate-50 rounded-lg">
                        <p className="text-sm text-slate-600">Response: {req.response_notes}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-8 text-center text-slate-500">
                <FileText className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                <p>No requests submitted</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Admin: All Data Requests Tab */}
      {activeTab === 'requests' && isAdmin && (
        <div className="bg-white border border-slate-200 rounded-xl">
          <div className="p-4 border-b border-slate-200">
            <h3 className="font-semibold text-slate-900">All Data Subject Requests</h3>
          </div>
          {dataRequests.length > 0 ? (
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-4 py-3 text-sm font-medium text-slate-700">User</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-slate-700">Type</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-slate-700">Status</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-slate-700">Date</th>
                  <th className="text-right px-4 py-3 text-sm font-medium text-slate-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {dataRequests.map((req) => (
                  <tr key={req.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3 text-sm text-slate-900">{req.user_email}</td>
                    <td className="px-4 py-3 text-sm text-slate-600">{requestTypeLabels[req.request_type]}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColors[req.status]}`}>
                        {req.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">
                      {format(new Date(req.requested_at), 'MMM dd, yyyy')}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {req.status === 'pending' && (
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => handleProcessRequest(req.id, 'completed')}
                            className="text-sm text-green-600 hover:text-green-700"
                          >
                            Complete
                          </button>
                          <button
                            onClick={() => handleProcessRequest(req.id, 'rejected')}
                            className="text-sm text-red-600 hover:text-red-700"
                          >
                            Reject
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="p-8 text-center text-slate-500">
              <FileText className="w-8 h-8 mx-auto mb-2 text-slate-300" />
              <p>No data requests</p>
            </div>
          )}
        </div>
      )}

      {/* Request Modal */}
      {showRequestModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4">
            <div className="p-6 border-b border-slate-200">
              <h3 className="text-lg font-semibold text-slate-900">Submit Data Request</h3>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Request Type</label>
                <select
                  value={requestForm.type}
                  onChange={(e) => setRequestForm({ ...requestForm, type: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                >
                  <option value="access">Access My Data</option>
                  <option value="rectification">Correct My Data</option>
                  <option value="erasure">Delete My Data</option>
                  <option value="portability">Export My Data</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Description (Optional)</label>
                <textarea
                  value={requestForm.description}
                  onChange={(e) => setRequestForm({ ...requestForm, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                  placeholder="Provide details about your request..."
                />
              </div>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowRequestModal(false)}
                  className="px-4 py-2 text-slate-700 hover:bg-slate-100 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmitRequest}
                  disabled={loading}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:bg-blue-300"
                >
                  Submit Request
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default POPIACompliance;
